import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { motion, AnimatePresence } from 'motion/react';

interface Node extends d3.SimulationNodeDatum {
  id: string;
  degree: number;
  isHub: boolean;
}

interface Link extends d3.SimulationLinkDatum<Node> {
  source: Node | string;
  target: Node | string;
}

// Generate connected DAG with power-law degree distribution (no cycles)
const generateScaleFreeNetwork = (n: number) => {
  const nodes: Node[] = [];
  const links: Link[] = [];
  const outDegrees: number[] = []; // Track number of children for each node by index

  // Create root node
  nodes.push({ id: '0', degree: 0, isHub: false });
  outDegrees.push(0);

  // Add remaining nodes as children, creating a connected tree
  for (let i = 1; i < n; i++) {
    const newNodeId = i.toString();
    nodes.push({ id: newNodeId, degree: 0, isHub: false });
    outDegrees.push(0);

    // Calculate cumulative weights for parent selection (preferential attachment)
    const weights: number[] = [];
    let cumulativeWeight = 0;

    for (let j = 0; j < i; j++) {
      const weight = outDegrees[j] + 1; // Preferential attachment: degree + 1
      cumulativeWeight += weight;
      weights.push(cumulativeWeight);
    }

    // Select parent using weighted random selection
    const rand = Math.random() * cumulativeWeight;
    let selectedParentIdx = 0;

    for (let j = 0; j < weights.length; j++) {
      if (rand <= weights[j]) {
        selectedParentIdx = j;
        break;
      }
    }

    const selectedParent = selectedParentIdx.toString();

    // Create link from parent to child (directed, no cycles)
    links.push({ source: selectedParent, target: newNodeId });
    outDegrees[selectedParentIdx]++;
  }

  // Calculate total degrees (in-degree + out-degree) for each node
  const inDegrees: number[] = new Array(n).fill(0);

  links.forEach(link => {
    const targetIdx = parseInt(typeof link.target === 'string' ? link.target : link.target.id);
    inDegrees[targetIdx]++;
  });

  nodes.forEach((node, idx) => {
    node.degree = inDegrees[idx] + outDegrees[idx];
  });

  // Identify hubs - nodes in top 10% by degree
  const sortedDegrees = nodes.map(n => n.degree).sort((a, b) => b - a);
  const hubThreshold = sortedDegrees[Math.floor(sortedDegrees.length * 0.1)];

  nodes.forEach(node => {
    node.isHub = node.degree >= hubThreshold && node.degree > 5;
  });

  return { nodes, links };
};

export const ScaleFreeNetwork: React.FC = () => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const nodesRef = useRef<Node[]>([]);
  const animationRunningRef = useRef(false);
  const [cursorPosition, setCursorPosition] = useState({ x: -100, y: -100 });
  const [showCursor, setShowCursor] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [hoveredNode, setHoveredNode] = useState<Node | null>(null);
  const [showTreeSummary, setShowTreeSummary] = useState(false);

  useEffect(() => {
    if (!svgRef.current) return;

    const width = svgRef.current.clientWidth;
    const height = svgRef.current.clientHeight;

    // Clear previous content
    d3.select(svgRef.current).selectAll('*').remove();

    // Generate scale-free network
    const { nodes, links } = generateScaleFreeNetwork(50);
    nodesRef.current = nodes;

    const svg = d3.select(svgRef.current);

    // Create force simulation with consistent edge lengths
    const simulation = d3.forceSimulation<Node>(nodes)
      .force('link', d3.forceLink<Node, Link>(links)
        .id((d: any) => d.id)
        .distance(35)
        .strength(2) // Strong link force to enforce uniform edge lengths
      )
      .force('charge', d3.forceManyBody<Node>().strength(-50))
      .force('center', d3.forceCenter(width / 2, height / 2).strength(0.05))
      .force('collision', d3.forceCollide<Node>().radius(7).strength(0.7));

    // Create links
    const linkSelection = svg.append('g')
      .selectAll('line')
      .data(links)
      .join('line')
      .attr('stroke', '#5a5a5a')
      .attr('stroke-width', 0.5)
      .attr('stroke-opacity', 0.6);

    // Create node groups - all same size
    const nodeSelection = svg.append('g')
      .selectAll('circle')
      .data(nodes)
      .join('circle')
      .attr('r', 5) // All nodes same size
      .attr('fill', '#0a9396') // All nodes teal/blue
      .attr('stroke', 'none')
      .attr('stroke-width', 0)
      .attr('opacity', 0.9);

    // Define boundary constraints
    const nodeRadius = 7; // Node radius + padding
    const padding = 10;

    // Update positions on tick with boundary constraints
    simulation.on('tick', () => {
      // Clamp node positions to stay within bounds
      nodes.forEach(node => {
        if (node.x !== undefined) {
          node.x = Math.max(padding + nodeRadius, Math.min(width - padding - nodeRadius, node.x));
        }
        if (node.y !== undefined) {
          node.y = Math.max(padding + nodeRadius, Math.min(height - padding - nodeRadius, node.y));
        }
      });

      linkSelection
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y);

      nodeSelection
        .attr('cx', (d: any) => d.x)
        .attr('cy', (d: any) => d.y);
    });

    return () => {
      simulation.stop();
    };
  }, []);

  // Intersection Observer for scroll-triggered animation
  useEffect(() => {
    const runAnimation = async () => {
      if (animationRunningRef.current || nodesRef.current.length === 0) return;
      animationRunningRef.current = true;

      // Wait for simulation to stabilize
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Find a hub node to hover over
      const hubNode = nodesRef.current.find((n: Node) => n.isHub);
      if (!hubNode || !hubNode.x || !hubNode.y) {
        animationRunningRef.current = false;
        return;
      }

      // Show cursor at initial position (bottom left)
      setShowCursor(true);
      setCursorPosition({ x: 50, y: 300 });
      await new Promise(resolve => setTimeout(resolve, 500));

      // Move cursor to node
      setCursorPosition({ x: hubNode.x, y: hubNode.y });
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Show tooltip
      setHoveredNode(hubNode);
      setShowTooltip(true);
      await new Promise(resolve => setTimeout(resolve, 2500));

      // Hide tooltip and cursor
      setShowTooltip(false);
      setHoveredNode(null);
      await new Promise(resolve => setTimeout(resolve, 300));
      setShowCursor(false);

      // Wait a bit then show tree summary
      await new Promise(resolve => setTimeout(resolve, 500));
      setShowTreeSummary(true);
      await new Promise(resolve => setTimeout(resolve, 3000));

      // Hide tree summary
      setShowTreeSummary(false);

      animationRunningRef.current = false;
    };

    // Intersection Observer to trigger animation on scroll
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !animationRunningRef.current) {
            runAnimation();
          }
        });
      },
      { threshold: 0.5 } // Trigger when 50% visible
    );

    const currentContainer = containerRef.current;
    if (currentContainer) {
      observer.observe(currentContainer);
    }

    return () => {
      if (currentContainer) {
        observer.unobserve(currentContainer);
      }
    };
  }, []);

  const getNodeSummary = (node: Node) => {
    if (node.isHub) {
      return "Climate mitigation strategies encompass renewable energy adoption, carbon capture technologies, and sustainable infrastructure development.";
    } else if (node.degree > 1) {
      return "Exploring various approaches to addressing environmental challenges through policy and technology.";
    } else {
      return "A specific aspect of the broader climate change discussion.";
    }
  };

  return (
    <div ref={containerRef} className="relative w-full h-full">
      <svg
        ref={svgRef}
        className="w-full h-full"
        style={{ background: 'transparent' }}
      />

      {/* Animated Cursor */}
      <AnimatePresence>
        {showCursor && (
          <motion.div
            className="absolute pointer-events-none"
            style={{
              zIndex: 100,
              left: 0,
              top: 0
            }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{
              x: cursorPosition.x - 12,
              y: cursorPosition.y - 12,
              opacity: 1,
              scale: 1
            }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{
              x: { type: "spring", stiffness: 100, damping: 15 },
              y: { type: "spring", stiffness: 100, damping: 15 },
              opacity: { duration: 0.3 },
              scale: { duration: 0.3 }
            }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="white" stroke="black" strokeWidth="1.5">
              <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
            </svg>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tooltip */}
      <AnimatePresence>
        {showTooltip && hoveredNode && (
          <motion.div
            className="absolute pointer-events-none"
            style={{
              zIndex: 70,
              left: `${(hoveredNode.x || 0) + 15}px`,
              top: `${(hoveredNode.y || 0) - 50}px`,
              maxWidth: '250px'
            }}
            initial={{ opacity: 0, scale: 0.9, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
          >
            <div className="bg-black/90 backdrop-blur-sm border border-white/20 rounded-lg p-3 shadow-xl">
              <div className="text-white/90 text-xs leading-relaxed">
                {getNodeSummary(hoveredNode)}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tree Summary Panel */}
      <AnimatePresence>
        {showTreeSummary && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, type: "spring", stiffness: 100 }}
            className="absolute rounded-lg shadow-2xl flex flex-col"
            style={{
              right: '3%',
              bottom: '3%',
              width: '280px',
              height: 'auto',
              maxHeight: '220px',
              backgroundColor: 'var(--bg-primary, #1a1a1a)',
              border: '1px solid rgba(255,255,255,0.1)',
              zIndex: 30,
            }}
          >
            {/* Header */}
            <div className="px-4 py-3 flex items-center justify-between border-b cursor-move" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-white/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <h3 className="text-sm font-semibold text-white">Tree Summary</h3>
              </div>
              <button className="p-1 rounded hover:bg-white/10 transition-colors">
                <svg className="w-4 h-4 text-white/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content - Plain Text */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="p-4 overflow-y-auto"
              style={{ fontSize: '10px', lineHeight: '1.6', color: 'rgba(255,255,255,0.7)' }}
            >
              <p style={{ marginBottom: '8px' }}>
                <strong style={{ color: 'rgba(255,255,255,0.9)' }}>Climate Change Solutions</strong>
              </p>
              <p style={{ marginBottom: '12px' }}>
                This exploration examines comprehensive approaches to addressing climate change, focusing on both
                mitigation strategies and adaptation methods across multiple technological and policy domains.
              </p>
              <p style={{ marginBottom: '8px' }}>
                <strong style={{ color: 'rgba(255,255,255,0.9)' }}>Key Topics:</strong>
              </p>
              <p style={{ marginBottom: '4px', paddingLeft: '12px' }}>
                • Renewable Energy - Solar and wind power solutions
              </p>
              <p style={{ marginBottom: '4px', paddingLeft: '12px' }}>
                • Carbon Capture - Technologies for CO2 removal
              </p>
              <p style={{ marginBottom: '12px', paddingLeft: '12px' }}>
                • Mitigation Strategies - Policy and implementation
              </p>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '9px' }}>
                3 branches • 8 nodes
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
